import { ComponentType, useContext, useMemo, useState } from "react";
import styled, { css } from "styled-components";

import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";
import { ModalOverlay } from "@com.mgmtp.a12.widgets/widgets-core";
import { Button } from "@com.mgmtp.a12.widgets/widgets-core/lib/button";
import { addPrefix } from "@com.mgmtp.a12.widgets/widgets-core/lib/common";
import { ActionContentbox, ContentBoxElements } from "@com.mgmtp.a12.widgets/widgets-core/lib/contentbox";
import { Icon } from "@com.mgmtp.a12.widgets/widgets-core/lib/icon";
import {
	ButtonGroupContainer,
	ButtonGroupContainerProps
} from "@com.mgmtp.a12.widgets/widgets-core/lib/layout/button-group-container";
import { Typography } from "@com.mgmtp.a12.widgets/widgets-core/lib/typography";

import { RESOURCE_KEYS } from "../localization";

import { ClientSelectionInput } from "./ClientSelectionInput";

const COMPONENT_RESOURCE_KEYS = RESOURCE_KEYS.component.modal.client;

export interface ClientModalProps {
	readonly description?: string;
	readonly client?: string;
	readonly clients: string[];

	onSubmit(client: string): void;
	onClose(): void;
}

export const ClientModal: ComponentType<ClientModalProps> = function ClientModal({
	description,
	client,
	clients,
	onSubmit,
	onClose
}) {
	const { localizer } = useContext(LocalizerContext);

	const [buffer, setBuffer] = useState(client ?? "");

	const footerButtons = useMemo<ButtonGroupContainerProps.ButtonProps[]>(
		() => [
			{
				label: localizer({ key: RESOURCE_KEYS.component.button.cancel.label }),
				icon: <Icon>close</Icon>,
				onClick: onClose
			},
			{
				label: localizer({ key: COMPONENT_RESOURCE_KEYS.button.confirm.new.label }),
				icon: <Icon>check</Icon>,
				primary: true,
				disabled: !clients.includes(buffer),
				onClick() {
					onSubmit(buffer.trim());
				}
			}
		],
		[localizer, buffer, clients, onSubmit, onClose]
	);

	return (
		<ModalOverlay onClose={onClose}>
			<ActionContentbox
				headingElements={
					<StyledContentBoxTitle text={localizer({ key: COMPONENT_RESOURCE_KEYS.title.new })} ariaLevel={1} />
				}
				headingButtons={
					<ContentBoxElements.HeadingAddon>
						<Button
							title={localizer({ key: RESOURCE_KEYS.component.modal.general.button.close.title })}
							icon={<Icon>close</Icon>}
							onClick={onClose}
						/>
					</ContentBoxElements.HeadingAddon>
				}
				footer={
					<ContentBoxElements.Footer>
						<ButtonGroupContainer rightSlotButtons={footerButtons} />
					</ContentBoxElements.Footer>
				}
				className={addPrefix("-u-rounded")}
			>
				<Typography.Section>
					{description && <Typography.Body>{description}</Typography.Body>}
					<ClientSelectionInput
						id="client-selection"
						client={buffer}
						clients={clients}
						onChange={setBuffer}
					/>
				</Typography.Section>
			</ActionContentbox>
		</ModalOverlay>
	);
};

const StyledContentBoxTitle = styled(ContentBoxElements.Title)`
	${({ ariaLevel, theme }) =>
		ariaLevel === 1
			? css`
					font-size: ${theme.typography.fontSize.hugeFontSize};
				`
			: ""}
`;
